
import { DashboardSidebar } from "./sidebar";
import { ReactNode, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Settings, User, LogOut } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface DashboardLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  actions?: ReactNode;
}

export function DashboardLayout({ 
  children, 
  title, 
  description,
  actions
}: DashboardLayoutProps) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<number>(3);
  const [sidebarVisible, setSidebarVisible] = useState<boolean>(true);

  // Log render for debugging
  useEffect(() => {
    console.log("[DashboardLayout] Rendering with sidebarVisible:", sidebarVisible);
  }, [sidebarVisible]);

  // Use React Query to cache and manage user data
  const { data: userData } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: Infinity, // Only refetch when explicitly invalidated
  });

  // Use React Query to cache and manage profile data
  const { data: profile } = useQuery({
    queryKey: ['userProfile', userData?.id],
    queryFn: async () => {
      if (!userData) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('full_name, avatar_url, username')
        .eq('id', userData.id)
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        return null;
      }
      
      return data;
    },
    staleTime: Infinity, // Only refetch when explicitly invalidated
    enabled: !!userData?.id,
  });

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen bg-background">
      {sidebarVisible && <DashboardSidebar notifications={notifications} />}
      
      {/* Main content container with conditional margin */}
      <div className={cn("flex-1 flex flex-col min-h-screen", sidebarVisible ? "ml-[280px]" : "ml-0")}>
        <header className="sticky top-0 border-b px-8 py-4 bg-background z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                className="md:hidden"
                onClick={() => setSidebarVisible(!sidebarVisible)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" x2="20" y1="12" y2="12"></line>
                  <line x1="4" x2="20" y1="6" y2="6"></line>
                  <line x1="4" x2="20" y1="18" y2="18"></line>
                </svg>
              </Button>
            </div>
            
            <div className="flex items-center gap-4">
              {/* This area is now empty as requested in previous changes */}
            </div>
          </div>
        </header>
        
        <main className="flex-1 overflow-auto">
          <div className="p-8">
            {(title || description || actions) && (
              <div className={cn(
                "pb-6 mb-8 border-b",
                description ? "flex flex-col gap-1" : "flex items-center justify-between"
              )}>
                <div>
                  {title && <h1 className="text-2xl font-heading font-bold">{title}</h1>}
                  {description && <p className="text-muted-foreground">{description}</p>}
                </div>
                
                {actions && <div className="flex items-center gap-4 mt-4 md:mt-0">{actions}</div>}
              </div>
            )}
            
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
